/**
 * Android Dimens Scaler Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

function roundTo(value: number, decimals: number): number {
  const p = 10 ** decimals;
  return Math.round(value * p) / p;
}

export const AndroidDimensScalerTool: ITransformerTool = {
  id: 'android-dimens-scaler',
  name: 'Android Dimens Scaler',
  description: 'Scale Android dimens XML values by a factor',
  version: '1.0.0',
  type: 'transformer',
  category: 'Calculators',
  keywords: ['android', 'dimens', 'xml', 'scale', 'dp', 'sp', 'calculator'],

  supportedInputTypes: [SEMANTIC_TYPES.XML, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.XML,

  attributes: [
    {
      name: 'scale',
      type: 'number',
      label: 'Scale',
      defaultValue: 1.0,
      validation: { min: 0.01, max: 100, step: 0.01 },
      description: 'Multiply all numeric values by this factor',
    },
    {
      name: 'decimals',
      type: 'number',
      label: 'Decimals',
      defaultValue: 2,
      validation: { min: 0, max: 6, step: 1 },
      description: 'Number of decimal places to keep',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const scale = typeof config.scale === 'number' ? (config.scale as number) : 1.0;
    const decimals = typeof config.decimals === 'number' ? (config.decimals as number) : 2;
    if (!Number.isFinite(scale) || scale <= 0) throw ToolError.validation('Scale must be > 0');

    const text = String(input ?? '');
    const out = text.replace(
      /(>\s*)([+-]?[0-9]*\.?[0-9]+)\s*(dp|dip|sp|px)\s*(<)/gi,
      (_m, pre: string, num: string, unit: string, post: string) => {
        const n = Number(num);
        if (!Number.isFinite(n)) return `${pre}${num}${unit}${post}`;
        const scaled = roundTo(n * scale, decimals);
        return `${pre}${scaled}${unit}${post}`;
      },
    );

    return out;
  },
};
